<script>
import { authComputed } from '@auth/helpers'
import { GoogleLogin } from 'vue-google-login'
import appConfig from '@src/app.config'

export default {
  name: 'LoginForm',
  components: { GoogleLogin },
  props: {
    authError: {
      type: String,
      required: true,
    },
    signUpError: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loginVisible: true,
      signupVisible: false,
      username: '',
      password: '',
      rememberMe: '',
      email: '',
      newPassword: '',
      fullName: '',
      zipCode: '',
      userCompany: '',
      errors: {
        email: '',
        password: '',
        name: '',
        zipCode: '',
        userCompany: '',
      },
      prettyAuthErrors: {
        invalid_credentials: 'Invalid Credentials',
        account_not_confirmed:
          'Please check your email and confirm your account first',
        provider_email_taken: 'Email already exists',
      },
      prettySignUpErrors: {
        email_taken: 'Email already exists',
      },
      googleParams: {
        client_id: appConfig.google_client_id,
      },
    }
  },
  computed: { ...authComputed },
  methods: {
    login() {
      this.$emit('post-login', {
        username: this.username,
        password: this.password,
        rememberMe: this.rememberMe,
      })
    },
    signup() {
      if (!this.email) {
        this.errors.email = 'Email required.'
      } else if (!this.validEmail(this.email)) {
        this.errors.email = 'Valid email required.'
      }
      if (!this.newPassword) {
        this.errors.password = 'Password required.'
      }
      if (!this.fullName) {
        this.errors.name = 'Full Name required.'
      }
      if (!this.zipCode) {
        this.errors.zipCode = 'Zip Code required.'
      }
      if (!this.userCompany) {
        this.errors.userCompany = 'Company Name required.'
      }
      if (!Object.values(this.errors).some((x) => x !== '')) {
        this.$emit('clear-sign-up-errors')
        this.$emit('post-signup', {
          email: this.email,
          newPassword: this.newPassword,
          fullName: this.fullName,
          zipCode: this.zipCode,
          userCompany: this.userCompany,
        })
      }
    },
    validEmail: function(email) {
      const rule = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return rule.test(email)
    },
    clearAuthErrors() {
      this.$emit('clear-auth-errors')
    },
    clearSignUpErrors(fieldName) {
      if (fieldName) {
        this.errors[fieldName] = ''
      }
      this.$emit('clear-sign-up-errors')
    },
    onSuccess(event) {
      const profile = event.getBasicProfile()
      this.$emit('post-login-with-provider', {
        name: profile.getName(),
        email: profile.getEmail(),
        image_url: profile.getImageUrl(),
        id_token: event.getAuthResponse().id_token,
        user_id: profile.getId(),
        login_provider: 'google',
      })
    },
  },
}
</script>

<template>
  <div>
    <h1 class="home-headline1"
      >Let's start sharing your community investment</h1
    >
    <h2 class="home-headline2">Login or Sign Up</h2>
    <div v-if="loginVisible">
      <div class="mt-8 flex -mx-2">
        <div class="w-1/2 flex-grow pl-2 pr-4 relative">
          <input
            v-model="username"
            type="text"
            name="email"
            placeholder="Username"
            class="input"
            :class="[authError ? 'border-red' : '']"
            @input="clearAuthErrors"
          />
          <svg
            v-if="authError"
            class="absolute right-0 top-0 mr-7 mt-2.5"
            xmlns="http://www.w3.org/2000/svg"
            width="17.433"
            height="15.57"
            viewBox="0 0 17.433 15.57"
          >
            <path
              class="a"
              fill="red"
              d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
              transform="translate(-1.898 -4.978)"
            />
          </svg>
        </div>
        <div class="w-1/2 flex-grow pl-4 pr-2 relative">
          <input
            v-model="password"
            type="password"
            name="password"
            placeholder="Password"
            class="input"
            :class="[authError ? 'border-red' : '']"
            @input="clearAuthErrors"
          />
          <svg
            v-if="authError"
            class="absolute right-0 top-0 mr-7 mt-2.5"
            xmlns="http://www.w3.org/2000/svg"
            width="17.433"
            height="15.57"
            viewBox="0 0 17.433 15.57"
          >
            <path
              class="a"
              fill="red"
              d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
              transform="translate(-1.898 -4.978)"
            />
          </svg>
          <span
            v-if="authError"
            class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
            >{{ prettyAuthErrors[authError] }}</span
          >
        </div>
      </div>
      <div class="mt-6 flex -mx-2 leading-xl">
        <div class="w-1/2 flex-grow pl-2 pr-4 flex content-start -mx-2">
          <div class="pl-2 pr-4">
            <button
              class="primary-button"
              :disabled="isTryingToLogin"
              @click="login"
              ><span v-if="isTryingToLogin">Loading</span
              ><span v-if="!isTryingToLogin">Login</span></button
            >
          </div>
          <div class="pl-3 pr-2">
            <router-link
              :to="{ name: 'login-by-phone' }"
              class="text-sm text-blue"
              >Login With My Cell Phone</router-link
            >
          </div>
        </div>
        <div class="w-1/2 flex-grow pl-4 pr-2 text-left">
          <router-link
            :to="{ name: 'forgot-password' }"
            class="text-sm text-blue ml-2"
            >Forgot Password?</router-link
          >
        </div>
      </div>
      <div class="mt-4 text-left">
        <label
          for="remember_me"
          class="text-sml text-blue cursor-pointer align-middle"
        >
          <input
            id="remember_me"
            v-model="rememberMe"
            type="checkbox"
            name="remember_me"
            class="checkbox mr-2"
          />
          Keep me signed in on this device
        </label>
      </div>
      <div v-if="!signupVisible && !successSignup">
        <GoogleLogin
          :params="googleParams"
          :on-success="onSuccess"
          class="w-48 h-10 mx-auto block mt-5 pt-1 bg-white text-google leading-xl text-sm font-roboto shadow"
          ><img
            src="~@assets/images/g-logo.png"
            class="w-5 h-5 inline-block mr-5 align-middle"
          /><span class="align-middle inline-block leading-xl font-bold"
            >Sign in with Google</span
          ></GoogleLogin
        >
        <span
          v-if="authError && authError === 'provider_email_taken'"
          class="mt-1.5 mr-7 text-red text-xxs capitalize text-center block w-full"
          >{{ prettyAuthErrors[authError] }}</span
        >
        <button
          class="w-48 h-10 mx-auto block mt-2 bg-blue-facebook text-white leading-xl text-xs font-roboto rounded-checkbox"
          ><img
            src="~@assets/images/f_logo_RGB-White_58.png"
            class="w-5 h-5 inline-block mr-4 align-middle"
          /><span class="align-middle inline-block leading-xl font-bold"
            >Continue with Facebook</span
          ></button
        >
        <button
          class="w-48 h-10 mx-auto block mt-2 bg-white text-sm text-blue leading-xl border border-blue border-solid rounded-button"
          @click="signupVisible = true"
          >Sign Up With Your Email</button
        >
      </div>
    </div>
    <div
      v-if="signupVisible && !successSignup"
      class="w-1/2 register-form mx-auto mt-10"
    >
      <div class="relative">
        <input
          v-model="email"
          type="text"
          name="email"
          placeholder="Email"
          class="input"
          :class="[
            signUpError === 'email_taken' || errors.email !== ''
              ? 'border-red'
              : '',
          ]"
          @input="clearSignUpErrors('email')"
        />
        <svg
          v-if="signUpError === 'email_taken' || errors.email !== ''"
          class="absolute right-0 top-0 mr-7 mt-2.5"
          xmlns="http://www.w3.org/2000/svg"
          width="17.433"
          height="15.57"
          viewBox="0 0 17.433 15.57"
        >
          <path
            class="a"
            fill="red"
            d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
            transform="translate(-1.898 -4.978)"
          />
        </svg>
        <span
          v-if="signUpError === 'email_taken'"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ prettySignUpErrors[signUpError] }}</span
        >
        <span
          v-if="errors.email !== ''"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ errors.email }}</span
        >
      </div>
      <div class="mt-7 relative">
        <input
          v-model="newPassword"
          type="password"
          name="newPassword"
          placeholder="Password"
          class="input"
          :class="[errors.password !== '' ? 'border-red' : '']"
          @input="clearSignUpErrors('password')"
        />
        <svg
          v-if="errors.password !== ''"
          class="absolute right-0 top-0 mr-7 mt-2.5"
          xmlns="http://www.w3.org/2000/svg"
          width="17.433"
          height="15.57"
          viewBox="0 0 17.433 15.57"
        >
          <path
            class="a"
            fill="red"
            d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
            transform="translate(-1.898 -4.978)"
          />
        </svg>
        <span
          v-if="errors.password !== ''"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ errors.password }}</span
        >
      </div>
      <div class="mt-7 relative">
        <input
          v-model="fullName"
          type="text"
          name="fullName"
          placeholder="Full Name"
          class="input"
          :class="[errors.name !== '' ? 'border-red' : '']"
          @input="clearSignUpErrors('name')"
        />
        <svg
          v-if="errors.name !== ''"
          class="absolute right-0 top-0 mr-7 mt-2.5"
          xmlns="http://www.w3.org/2000/svg"
          width="17.433"
          height="15.57"
          viewBox="0 0 17.433 15.57"
        >
          <path
            class="a"
            fill="red"
            d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
            transform="translate(-1.898 -4.978)"
          />
        </svg>
        <span
          v-if="errors.name !== ''"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ errors.name }}</span
        >
      </div>
      <div class="mt-7 relative">
        <input
          v-model="zipCode"
          type="text"
          name="zipCode"
          placeholder="Zip Code"
          class="input"
          :class="[errors.name !== '' ? 'border-red' : '']"
          @input="clearSignUpErrors('name')"
        />
        <svg
          v-if="errors.zipCode !== ''"
          class="absolute right-0 top-0 mr-7 mt-2.5"
          xmlns="http://www.w3.org/2000/svg"
          width="17.433"
          height="15.57"
          viewBox="0 0 17.433 15.57"
        >
          <path
            class="a"
            fill="red"
            d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
            transform="translate(-1.898 -4.978)"
          />
        </svg>
        <span
          v-if="errors.zipCode !== ''"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ errors.zipCode }}</span
        >
      </div>
      <div class="mt-7 relative">
        <select
          v-model="userCompany"
          required
          name=""
          class="select h-sm"
          :class="[errors.name !== '' ? 'border-red' : '']"
          @input="clearSignUpErrors('name')"
        >
          <option value="" disabled selected>Company Name</option>
          <option value="0">My company is not listed here</option>
          <option
            v-for="company in companiesForLogin"
            :key="company.id"
            :value="company.id"
            >{{ company.name }}</option
          >
        </select>
        <svg
          v-if="errors.userCompany !== ''"
          class="absolute right-0 top-0 mr-7 mt-2.5"
          xmlns="http://www.w3.org/2000/svg"
          width="17.433"
          height="15.57"
          viewBox="0 0 17.433 15.57"
        >
          <path
            class="a"
            fill="red"
            d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
            transform="translate(-1.898 -4.978)"
          />
        </svg>
        <span
          v-if="errors.userCompany !== ''"
          class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
          >{{ errors.userCompany }}</span
        >
      </div>
      <div class="mt-7 text-left">
        <button
          class="primary-button"
          :disabled="isTryingToSignup"
          @click="signup"
          ><span v-if="isTryingToSignup">Loading</span
          ><span v-if="!isTryingToSignup">Sign Up</span></button
        >
      </div>
    </div>
    <div v-if="successSignup" class="success-message mx-auto mt-5">
      <div class="mt-5 text-basel text-black">
        You have succesfully signed up! Please check you email for the
        confirmation email!
      </div>
    </div>
  </div>
</template>
